import { useQuery } from "@tanstack/react-query";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Coins, Percent, Receipt } from "@phosphor-icons/react";
import type { DateRangeValue } from "@/components/admin/DateRangePicker";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { throwIfNotOk } from "@/lib/api-error";
import type { SubscriptionsReportResponse } from "./types";
import { ErrorCard, km, LoadingState, Metric, shortDate } from "./report-shared";

const chartConfig = {
  gross: { label: "Bruto", color: "var(--chart-1)" },
} satisfies ChartConfig;

export function SubscriptionsReportBody({ dateRange }: { dateRange: DateRangeValue }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "reports", "subscriptions", dateRange.from, dateRange.to],
    queryFn: async () => {
      const params = new URLSearchParams({ from: dateRange.from, to: dateRange.to });
      const res = await fetch(`/api/admin/reports/subscriptions?${params}`);
      await throwIfNotOk(res, "Greška pri učitavanju izvještaja o pretplatama");
      return res.json() as Promise<SubscriptionsReportResponse>;
    },
  });

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorCard message={(error as Error).message} />;
  if (!data) return null;

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Metric
          tone="primary"
          icon={Coins}
          label="Ukupno bruto"
          value={km(data.totalGross)}
          detail={`${data.count} pretplata`}
        />
        <Metric
          icon={Receipt}
          label="Neto"
          value={km(data.totalNet)}
          detail="Bez PDV-a"
        />
        <Metric
          icon={Percent}
          label="PDV"
          value={km(data.totalVat)}
          detail="Ukupan PDV"
        />
        <Metric
          icon={Receipt}
          label="Broj pretplata"
          value={String(data.count)}
          detail="Naplaćeno u periodu"
        />
      </div>

      <ChartContainer config={chartConfig} className="h-[320px] w-full">
        <BarChart accessibilityLayer data={data.series} margin={{ left: 4, right: 8, top: 8 }}>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={false}
            tickMargin={10}
            minTickGap={24}
            tickFormatter={shortDate}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            width={64}
            tickFormatter={(value) => `${Number(value).toLocaleString("bs-BA")} KM`}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                indicator="dot"
                labelFormatter={(value) => shortDate(String(value))}
                formatter={(value) => (
                  <div className="flex min-w-36 items-center justify-between gap-4">
                    <span className="text-muted-foreground">Bruto</span>
                    <span className="font-mono font-medium tabular-nums text-foreground">
                      {km(Number(value))}
                    </span>
                  </div>
                )}
              />
            }
          />
          <Bar dataKey="gross" fill="var(--color-gross)" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ChartContainer>
    </>
  );
}
